"use client";
import Button from "../components/Button";
import Section from "../components/Section";
import Card from "../components/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNetworkWired } from "@fortawesome/free-solid-svg-icons/faNetworkWired";
import { faSitemap } from "@fortawesome/free-solid-svg-icons/faSitemap";
import { faBrain } from "@fortawesome/free-solid-svg-icons/faBrain";

const TelecomHeroArt = () => (
  <svg
    viewBox="0 0 800 600"
    role="img"
    aria-label="Telecom fiber network visualization"
    className="w-full h-auto drop-shadow-2xl"
    preserveAspectRatio="xMidYMid meet">
    <defs>
      <radialGradient id="halo" cx="50%" cy="45%" r="60%">
        <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.35" />
        <stop offset="55%" stopColor="#6366f1" stopOpacity="0.22" />
        <stop offset="100%" stopColor="#1e293b" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="fiber" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#38bdf8" />
        <stop offset="50%" stopColor="#6366f1" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
      <linearGradient id="beam" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#60a5fa" />
        <stop offset="100%" stopColor="#7c3aed" />
      </linearGradient>
      <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="2.4" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <mask id="fade">
        <radialGradient id="fade-g" cx="50%" cy="50%" r="60%">
          <stop offset="75%" stopColor="white" />
          <stop offset="100%" stopColor="black" />
        </radialGradient>
        <rect width="100%" height="100%" fill="url(#fade-g)" />
      </mask>
    </defs>

    <style>{`
      @keyframes dash { to { stroke-dashoffset: -1200; } }
      @keyframes pulse {
        0% { opacity: .55 } 50% { opacity: .95 } 100% { opacity: .55 }
      }
    `}</style>

    <rect x="0" y="0" width="800" height="600" fill="url(#halo)" />

    <g mask="url(#fade)">
      <circle cx="400" cy="300" r="190" fill="none" stroke="url(#fiber)" strokeWidth="8" filter="url(#glow)" />
      <circle cx="400" cy="300" r="140" fill="none" stroke="url(#beam)" strokeWidth="2" opacity="0.35" />
      {[0.75, 0.6, 0.45, 0.33].map((ry, i) => (
        <ellipse key={`lat-${i}`} cx="400" cy="300" rx="260" ry={260 * ry} fill="none" stroke="url(#beam)" strokeWidth="1.2" opacity={0.28 - i * 0.05} filter="url(#glow)" />
      ))}
      {[
        "M120,320 C260,120 540,140 680,300",
        "M110,260 C260,200 550,380 690,340",
        "M150,420 C280,360 520,260 660,280",
      ].map((d, i) => (
        <path key={`fiber-${i}`} d={d} fill="none" stroke="url(#fiber)" strokeWidth={i === 1 ? 3 : 2} strokeDasharray="12 10" style={{ animation: `dash ${10 + i * 2}s linear infinite` }} filter="url(#glow)" opacity={0.85 - i * 0.15} />
      ))}
      {[
        [200, 210],
        [610, 220],
        [520, 410],
        [290, 430],
        [420, 330],
      ].map(([x, y], idx) => (
        <g key={`node-${idx}`} style={{ animation: "pulse 3.2s ease-in-out infinite" }}>
          <circle cx={x} cy={y} r="5" fill="#38bdf8" />
          <circle cx={x} cy={y} r="10" fill="#38bdf8" opacity="0.22" />
        </g>
      ))}
      <circle cx="400" cy="300" r="10" fill="#8b5cf6" />
      <circle cx="400" cy="300" r="24" fill="#8b5cf6" opacity="0.2" />
    </g>
  </svg>
);

const Home = ({ onNavigate = () => {} }) => {
  const features = [
    {
      title: "Global Infrastructure",
      description:
        "Advanced telecommunications backbone spanning continents with redundant systems and enterprise grade reliability that powers global business communications.",
      icon: <FontAwesomeIcon icon={faNetworkWired} aria-hidden="true" />,
    },
    {
      title: "Enterprise Communication",
      description:
        "Comprehensive unified communications platform for modern enterprises, integrating voice, data, and messaging into seamless workflows.",
      icon: <FontAwesomeIcon icon={faSitemap} aria-hidden="true" />,
    },
    {
      title: "Intelligent Network Operations",
      description:
        "AI-powered monitoring and optimization with predictive analytics ensuring peak performance and proactive issue resolution 24/7.",
      icon: <FontAwesomeIcon icon={faBrain} aria-hidden="true" />,
    },
  ];

  const innovations = [
    {
      title: "Next-Gen Network Architecture",
      description:
        "Software-defined networking with edge compute for ultra-low latency communications.",
      metric: "< 5ms latency",
    },
    {
      title: "Quantum-Safe Security",
      description:
        "Future-proof encryption to protect your communications against emerging quantum threats.",
      metric: "Enterprise grade",
    },
    {
      title: "AI-Driven Optimization",
      description:
        "Machine learning continuously tunes routing and performance across our global network.",
      metric: "99.99% uptime",
    },
  ];

  return (
    <div className="animate-fade-in overflow-x-hidden">
      <Section className="pt-28 lg:pt-32 pb-20 lg:pb-24 overflow-visible bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 tracking-tight leading-[1.15] break-words">
              Our Route to
              <span className="block bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mt-3 pb-[0.22em] -mb-[0.22em] [-webkit-text-fill-color:transparent]">
                Your Destination
              </span>
            </h1>

            <p
              className="text-lg sm:text-xl md:text-2xl text-gray-700 font-light leading-relaxed max-w-2xl mx-auto lg:mx-0"
              style={{ animationDelay: "0.1s" }}>
              Carrier-grade voice & SMS, SIP routing, and real-time QoS
              analytics — engineered for scale and reliability.
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center lg:justify-start"
              style={{ animationDelay: "0.2s" }}>
              <Button
                size="lg"
                onClick={() => onNavigate("contact")}
                className="text-base sm:text-lg md:text-xl px-8 sm:px-10 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                Start Your Journey
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => onNavigate("partners")}
                className="text-base sm:text-lg md:text-xl px-8 sm:px-10 py-5 border-2 border-gray-300 hover:border-blue-500">
                Explore Partnerships
              </Button>
            </div>
          </div>

          {/* CLIPPED GLOW ON MOBILE TO PREVENT H-SCROLL */}
          <div className="relative overflow-hidden rounded-[2rem]">
            <div
              className="absolute inset-0 sm:-inset-6 bg-gradient-to-tr from-blue-200/35 via-indigo-200/25 to-purple-200/25 blur-2xl rounded-full"
              aria-hidden="true"
            />
            <div className="relative mx-auto w-[min(560px,90vw)]">
              <div className="aspect-square rounded-3xl border border-slate-200 bg-white/80 backdrop-blur p-4 shadow-xl">
                <div className="aspect-square rounded-2xl bg-gradient-to-br from-slate-50 to-white grid place-items-center overflow-hidden">
                  <div className="w-[92%]">
                    <TelecomHeroArt />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section className="py-32 overflow-visible">
        <div className="text-center mb-24 space-y-12 px-4">
          <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 tracking-tight leading-[1.15] break-words">
            Innovation at
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mt-6 pb-[0.18em] -mb-[0.18em] [-webkit-text-fill-color:transparent]">
              Every Layer
            </span>
          </h2>

          <p className="text-xl sm:text-2xl md:text-3xl text-gray-600 max-w-5xl mx-auto leading-relaxed">
            Cutting-edge technology meets enterprise reliability in our
            next-generation telecommunications platform.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-7xl mx-auto mb-20 px-4">
          {innovations.map((innovation) => (
            <Card
              key={innovation.title}
              hover
              className="text-center p-10 space-y-8 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-500 hover:shadow-2xl">
              <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl p-6 mx-auto w-fit">
                <div className="text-4xl font-bold text-blue-600">
                  {innovation.metric}
                </div>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                {innovation.title}
              </h3>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                {innovation.description}
              </p>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto px-4">
          {features.map((feature) => (
            <Card
              key={feature.title}
              hover
              className="group text-left p-10 space-y-6 bg-gradient-to-br from-white to-blue-50 border-2 border-blue-100 hover:border-blue-400 transition-all duration-300">
              <div
                className="bg-blue-600 text-white rounded-xl p-4 w-fit text-2xl"
                aria-hidden="true"
                role="img">
                {feature.icon}
              </div>

              <div className="min-w-0">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight [overflow-wrap:anywhere] hyphens-auto">
                  {feature.title}
                </h3>
              </div>

              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* LAST SECTION — MOBILE FIT + NO H-SCROLL */}
      <Section className="py-32 relative overflow-visible bg-gradient-to-br from-blue-700 via-indigo-700 to-indigo-800 text-white">
        <div className="text-center space-y-12 max-w-5xl mx-auto relative z-10 px-4">
          <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-[1.12] break-words">
            Ready to Transform
            <span className="block text-blue-200 mt-6 pb-[0.14em] -mb-[0.14em]">
              Your Communications?
            </span>
          </h2>

          <p className="text-lg sm:text-2xl md:text-3xl text-blue-100 leading-relaxed">
            Join industry leaders who trust Faef Global to power their global
            communications infrastructure with unmatched reliability and
            innovation.
          </p>

          <div className="pt-8">
            <Button
              size="lg"
              onClick={() => onNavigate("contact")}
              className="w-full sm:w-auto text-lg sm:text-xl md:text-2xl px-8 sm:px-12 py-5 sm:py-6 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-600 text-white shadow-2xl shadow-blue-900/30 ring-1 ring-white/10 transition-all duration-300">
              Begin Your Transformation
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Home;
